import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { SIFRE_SIFIRLAMA_DAKIKA } from "@koclukpanel/contracts";
import { Alan, Buton, GirisKabugu, NotKutusu, SistemSayfasi, anMetni } from "@koclukpanel/ui";
import { ApiHatasi, sifirlamaIste } from "./api";

/**
 * SİSTEM SAYFALARI — hata, kimlik ve durum ekranları
 *
 * ================================================================
 * ON EKRAN, HEPSİ 04.09.2026'DA YAZILDI
 * ================================================================
 * Onu da teslim paketinde çizilmişti ve hiçbiri yazılmamıştı. Ortak
 * özellikleri, hepsinin **menüden erişilememesi**: boşluk taraması
 * onları göremiyor (uç yok, istemci yok), görev listesinde ayrı
 * madde değiller ve normal kullanımda hiç açılmıyorlar.
 *
 * Tam da bu yüzden atlandılar — ve tam da bu yüzden en kötü anda
 * ortaya çıkacaklardı: kullanıcı hata alınca, oturumu düşünce,
 * hesabı kilitlenince.
 *
 * Kabuk `packages/ui/src/SistemSayfasi.tsx`. Buradakiler ince:
 * metin, ızgara işareti ve düğmeler.
 */

// ---------------------------------------------------------------
// SİSTEM HATALARI
// ---------------------------------------------------------------

export function Hata404({ onBugun }: { onBugun: () => void }) {
  return (
    <SistemSayfasi
      rozet="404"
      isaret="arayan"
      baslik="Aradığınız sayfa burada değil."
      aciklama="Bağlantı eskimiş ya da kayıt silinmiş olabilir. Adresi elle yazdıysanız bir harf kaymış olabilir."
      eylemler={
        <>
          <Buton onClick={onBugun}>Bugün&apos;e dön</Buton>
          <Buton gorunum="ikincil" onClick={() => window.history.back()}>
            Geri
          </Buton>
        </>
      }
      altNot={
        <>
          {/*
            YANLIŞ ÇIKARIMI ÖNCEDEN DÜZELTİYOR.
            404 gören kullanıcının ilk düşüncesi "yetkim yok" oluyor
            ve destek talebi orada açılıyor. İki durumu ayırmak, o
            talebi hiç açtırmıyor.
          */}
          Bu bir yetki sorunu <strong className="text-notr-800">değil</strong> — erişiminiz olsaydı
          da bu sayfa yoktu. Yetkisiz bir sayfa <em>403</em> döner.
        </>
      }
    />
  );
}

export function Hata403({ onBugun }: { onBugun: () => void }) {
  return (
    <SistemSayfasi
      rozet="403"
      isaret="kilitli"
      baslik="Bu kayıt var, ama size kapalı."
      aciklama="Kaydın kendisi duruyor; onu görme yetkiniz yok. Yetki gerektiğini düşünüyorsanız yöneticinize söyleyin."
      eylemler={
        <>
          <Buton onClick={onBugun}>Bugün&apos;e dön</Buton>
          <Buton gorunum="ikincil" onClick={() => window.history.back()}>
            Geri
          </Buton>
        </>
      }
      altNot={
        <>
          {/*
            "VAR AMA SANA KAPALI" — bu projenin tekrar eden ayrımı.
            "Bulunamadı" demek daha güvenli görünür ama yanlış:
            kullanıcı olmayan bir kaydı arar durur.
          */}
          Bu ekran kaydın <strong className="text-notr-800">var olduğunu</strong> söylüyor,
          içeriğini değil. Var olmayan bir kayıt <em>404</em> döner.
        </>
      }
    />
  );
}

export function Hata500({ onYenile }: { onYenile: () => void }) {
  return (
    <SistemSayfasi
      rozet="500"
      isaret="kirik"
      baslik="Sunucu bu isteği tamamlayamadı."
      /*
        ================================================================
        "HATA KAYDEDİLDİ VE TEKNİK YÖNETİCİYE DÜŞTÜ" YAZIYORDU — YALANDI
        ================================================================
        Denetim bulgusu, 04.09.2026. Bu ekranın tek çağıranı
        `HataSiniri` ve orası hatayı hiçbir yere GÖNDERMİYOR, yalnızca
        `console.error` yazıyor. Göndermemek bilinçli (F-LOG-14 —
        yığın izinde kişisel veri olabilir), ama cümle düzeltilmemişti.

        Zararı somut: kullanıcı "zaten bildirilmiş" diye kimseye
        söylemez, hata hiçbir yere düşmez ve tekrarlanabilir bir çökme
        aylarca görünmez kalır.

        Yeni cümle kullanıcıya YAPMASI GEREKENİ söylüyor. Bu, bu
        commit'in kendi düzelttiği hata sınıfının aynısıydı:
        "Bağlantı 7 gün geçerlidir" de yalandı, sunucudaki değer
        30 dakikaydı.
      */
      aciklama="Aynı işlemi birkaç dakika sonra tekrar deneyebilirsiniz. Sorun sürerse ekran görüntüsüyle birlikte teknik yöneticinize bildirin — bu hata otomatik olarak kimseye ulaşmıyor."
      eylemler={
        <>
          <Buton onClick={onYenile}>Tekrar dene</Buton>
          <Buton gorunum="ikincil" onClick={() => window.history.back()}>
            Geri
          </Buton>
        </>
      }
      altNot={
        <>
          {/*
            VERİ KAYBI SORUSUNU ÖNCEDEN CEVAPLIYOR — 500 görenin ilk
            korkusu bu. Cevabı bilmek, formu baştan doldurmaktan
            kurtarıyor.
          */}
          Yazma işlemleri ya tamamen tamamlanır ya hiç yazılmaz. Yarım kalmış bir kayıt oluşmaz;
          girdiğiniz veriler kaybolmuşsa yeniden girilmeleri gerekir.
        </>
      }
    />
  );
}

// ---------------------------------------------------------------
// SİSTEM DURUMU
// ---------------------------------------------------------------

export function BakimModu({ mesaj, bitis }: { mesaj?: string | null; bitis?: string | null }) {
  return (
    <SistemSayfasi
      rozet="Planlı bakım"
      isaret="bekleyen"
      baslik="Sistem şu an kapalı."
      aciklama={
        <>
          {mesaj ?? "Planlı bakım sürüyor. Bu sırada panele giriş yapılamıyor."}
          {/*
            BİTİŞ SAATİ VARSA YAZILIYOR, YOKSA UYDURULMUYOR.
            "Birazdan açılacak" demek, on dakika mı iki saat mi
            bilmeyen kullanıcıyı ekran başında bekletir.
          */}
          {bitis && (
            <>
              {" "}
              Tahmini açılış: <strong>{anMetni(bitis)}</strong>.
            </>
          )}
        </>
      }
      eylemler={<Buton onClick={() => window.location.reload()}>Yeniden dene</Buton>}
      altNot="Bakım sırasında verilerinize hiçbir şey olmaz. Girilmiş kayıtlar yerinde durur."
    />
  );
}

export function Cevrimdisi() {
  return (
    <SistemSayfasi
      rozet="Bağlantı yok"
      isaret="kirik"
      baslik="İnternet bağlantısı kesildi."
      aciklama="Panel sunucuya ulaşamıyor. Bağlantınız döndüğünde kaldığınız yerden devam edebilirsiniz."
      eylemler={<Buton onClick={() => window.location.reload()}>Yeniden dene</Buton>}
      altNot={
        <>
          {/*
            PANEL ÇEVRİMDIŞI ÇALIŞMIYOR ve bunu söylemek gerekiyor:
            öğrenci uygulaması çalışıyor (PWA, kuyruk) ve kullanıcı
            ikisini aynı sanabilir.
          */}
          Panel çevrimdışı çalışmaz. Öğrenci uygulaması çalışır: orada girilen veri bağlantı dönünce
          kendiliğinden gönderilir.
        </>
      }
    />
  );
}

export function TarayiciDesteklenmiyor() {
  return (
    <SistemSayfasi
      rozet="Desteklenmeyen tarayıcı"
      isaret="kirik"
      baslik="Bu tarayıcı arayüzü doğru çizemiyor."
      aciklama="Panel güncel bir tarayıcı gerektiriyor. Chrome, Edge, Firefox ya da Safari'nin son sürümüyle açın."
      altNot={
        <>
          {/*
            NEDEN ENGELLEDİĞİMİZ YAZILI. "Desteklenmiyor" demek ve
            susmak, kullanıcıya "bizim hatamız" hissi verir; oysa
            eski tarayıcıda ekran YANLIŞ çizilir ve o daha kötüdür.
          */}
          Engellememizin sebebi: eski tarayıcıda ekran yanlış çizilir ve yanlış çizilmiş bir ekranda
          hangi verinin doğru olduğu anlaşılmaz.
        </>
      }
    />
  );
}

// ---------------------------------------------------------------
// KİMLİK
// ---------------------------------------------------------------

/*
  ================================================================
  KİMLİK EKRANLARI GİRİŞ KABUĞUNU KULLANIYOR, SİSTEM KABUĞUNU DEĞİL
  ================================================================
  Çizim paketi ikiye ayırıyor ve ayrım tesadüf değil:

    SistemSayfasi  404 · 403 · 500 · bakım · çevrimdışı · tarayıcı
                   → "sistem bir şey söylüyor"
    GirisKabugu    çıkış · oturum düştü · hesap kilitli · şifremi
                   unuttum · jeton geçersiz
                   → "giriş akışının bir adımındasın"

  İkincilerde kullanıcı zaten giriş yapmaya çalışıyor ve bir sonraki
  hamlesi yine giriş. Aynı kabuğu görmesi, nerede olduğunu
  kaybettirmiyor: sol paneldeki hafta duruyor, sağdaki kutu
  değişiyor.
*/

export function OturumSuresiDoldu({ onGiris }: { onGiris: () => void }) {
  return (
    <GirisKabugu
      isaret={{ ikon: "bekliyor", ton: "uyari" }}
      baslik="Oturum süresi doldu"
      /*
        ÇİZİMDEKİ "45 dakika" YAZILMADI. Süre ortam değişkeninden
        geliyor (`SESSION_IDLE_MINUTES_STAFF`) ve kuruma göre
        değişiyor; sabit bir sayı yazmak, ayar değiştiği gün sessizce
        yalan söyleyen bir cümle bırakırdı. Sunucu bu değeri arayüze
        hiç göndermiyor — göndermesi gerektiğinde cümle sayıyı alır.
      */
      aciklama="Güvenlik için bir süre işlem yapılmayan oturumlar kapatılıyor."
      altNot={
        <>
          {/*
            KAYDEDİLMEMİŞ VERİ SORUSU. Oturumu düşen kullanıcının ilk
            düşüncesi "yazdıklarım gitti mi" oluyor ve cevabı
            bilmemek, aynı işi baştan yapmaya iter.

            Çizim burada "kaydedilmemiş koçluk notu tarayıcıda
            tutuldu" diyor. YAZILMADI: taslak saklama diye bir şey
            yok. Tutulmayan bir taslağı tutuldu demek, kullanıcıyı
            geri geldiğinde boş bir formla karşılaştırır — ve o an
            sisteme bir daha güvenmez.
          */}
          Kaydettiğiniz her şey duruyor. Yalnızca{" "}
          <strong className="text-notr-800">kaydetmeden bıraktığınız</strong> form içerikleri
          kaybolmuş olabilir.
        </>
      }
    >
      <Buton boyut="genis" onClick={onGiris}>
        Giriş yap
      </Buton>
    </GirisKabugu>
  );
}

/**
 * HESAP KİLİTLİ — G-AUT-06
 *
 * Sunucu kilit yanıtında kalan dakikayı veriyor; ekran onu geri
 * sayıyor. Geri sayım SÜSLEME DEĞİL: "biraz sonra deneyin" diyen
 * bir ekran, kullanıcıyı her dakika bir kez daha denemeye
 * gönderiyor ve her deneme kilidi uzatabiliyor.
 */
export function HesapKilitli({
  kalanDakika,
  onGiris,
  onSifremiUnuttum,
}: {
  kalanDakika: number | null;
  onGiris: () => void;
  onSifremiUnuttum: () => void;
}) {
  /*
    Bitiş anı BİR KEZ hesaplanıyor. Her saniye "şimdi + kalanDakika"
    deseydik sayaç hiç azalmazdı — sayacın azalması için sabit bir
    hedefe göre ölçmek gerekiyor.
  */
  const [bitis] = useState(() => (kalanDakika === null ? null : Date.now() + kalanDakika * 60_000));
  const [simdi, setSimdi] = useState(() => Date.now());

  useEffect(() => {
    if (bitis === null) return;
    const s = setInterval(() => setSimdi(Date.now()), 1000);
    return () => clearInterval(s);
  }, [bitis]);

  const kalanSaniye = bitis === null ? null : Math.max(0, Math.ceil((bitis - simdi) / 1000));
  const bitti = kalanSaniye !== null && kalanSaniye === 0;
  const dakika = kalanSaniye === null ? 0 : Math.floor(kalanSaniye / 60);
  const saniye = kalanSaniye === null ? 0 : kalanSaniye % 60;

  return (
    <GirisKabugu
      isaret={{ ikon: "kilit", ton: "hata" }}
      baslik="Çok fazla başarısız deneme"
      aciklama={
        kalanDakika === null ? (
          "Hesap bir süre girişe kapatıldı."
        ) : (
          <>
            Hesap <strong className="text-notr-800">{kalanDakika} dakika</strong> boyunca girişe
            kapatıldı.
          </>
        )
      }
      altNot={
        <>
          Kilit <strong className="text-notr-800">kendiliğinden</strong> açılır; yöneticiye
          başvurmanız gerekmez. Doğru şifreyi girmek süreyi kısaltmaz.
        </>
      }
    >
      {kalanSaniye !== null && (
        <div className="rounded-alan bg-notr-50 flex flex-col gap-2.5 px-4 py-4">
          <div className="flex items-baseline justify-between">
            <span className="text-notr-700 text-[13px]">Kalan süre</span>
            {/*
              `aria-live="off"`: saniyede bir güncellenen bir sayıyı
              duyurmak ekran okuyucu kullanıcısını sürekli sözünü
              kesen bir anonsa mahkûm ederdi. Bilgi zaten açıklamada
              metin olarak duruyor.
            */}
            <span aria-live="off" className="text-notr-900 text-[22px] font-semibold">
              {dakika}:{String(saniye).padStart(2, "0")}
            </span>
          </div>
          <div className="rounded-rozet bg-notr-200 h-[5px] overflow-hidden">
            <div
              className="rounded-rozet bg-notr-400 ease-cikis h-full transition-[width] duration-(--sure-normal)"
              style={{
                width: `${Math.round((kalanSaniye / Math.max(1, (kalanDakika ?? 1) * 60)) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      <NotKutusu ton="hata">
        Giriş denemesi <strong>siz yapmadıysanız</strong> şifreniz başkasının elinde olabilir. Süre
        dolunca girip şifrenizi değiştirin.
      </NotKutusu>

      {/*
        ŞİFRESİNİ UNUTMUŞ OLABİLİR — asıl sebep çoğunlukla bu.
        Sıfırlama kilitliyken de çalışıyor: kilit ŞİFRE DENEMESİNİ
        durduruyor, e-posta ile kimlik doğrulamayı değil.
      */}
      <Buton gorunum="ikincil" boyut="genis" onClick={onSifremiUnuttum}>
        Şifremi sıfırla
      </Buton>

      {bitti && (
        <Buton boyut="genis" onClick={onGiris}>
          Yeniden dene
        </Buton>
      )}
    </GirisKabugu>
  );
}

export function CikisYapildi({ onGiris }: { onGiris: () => void }) {
  return (
    <GirisKabugu
      isaret={{ ikon: "tamam", ton: "onay" }}
      baslik="Çıkış yapıldı"
      aciklama={
        <>
          Oturumunuz kapatıldı ve <strong className="text-notr-800">bu cihazdaki</strong> yerel
          veriler temizlendi.
        </>
      }
      altNot={
        <>
          Ortak bilgisayarda çalışıyorsanız{" "}
          <strong className="text-notr-800">tarayıcıyı tamamen kapatın</strong>.
        </>
      }
    >
      <NotKutusu ton="bilgi">
        Diğer cihazlardaki oturumlarınız <strong>açık kalmaya devam eder</strong>. Hepsini kapatmak
        için Hesabım → Oturumlar.
      </NotKutusu>

      <Buton boyut="genis" onClick={onGiris}>
        Yeniden giriş yap
      </Buton>
    </GirisKabugu>
  );
}

/**
 * ŞİFREMİ UNUTTUM — sıfırlama İSTEĞİ
 *
 * ================================================================
 * KULLANICI KENDİ ŞİFRESİNİ SIFIRLAYAMIYORDU
 * ================================================================
 * `POST /auth/sifre-sifirlama-istegi` yazılıydı ve panelde tek
 * çağıranı Personel ekranıydı — yani YÖNETİCİNİN başkası için
 * tetiklemesi. Şifresini unutan kullanıcının kendi başına
 * yapabileceği hiçbir şey yoktu: yöneticiyi araması gerekiyordu.
 *
 * ================================================================
 * SUNUCUNUN CÜMLESİ AYNEN GÖSTERİLİYOR
 * ================================================================
 * Uç, adres kayıtlı olmasa da aynı cevabı veriyor ("bu adres
 * kayıtlıysa…"). Farklı cevap verseydi, bu uç bir e-postanın
 * sistemde olup olmadığını sorgulamak için kullanılabilirdi.
 * Ekran da kendi cümlesini uydurmuyor.
 */
export function SifremiUnuttum({ onGiris }: { onGiris: () => void }) {
  const [eposta, setEposta] = useState("");
  const [mesaj, setMesaj] = useState<string | null>(null);

  const gonder = useMutation({
    mutationFn: () => sifirlamaIste(eposta.trim()),
    onSuccess: (c) => setMesaj(c.mesaj),
  });

  if (mesaj !== null) {
    return (
      <GirisKabugu
        isaret={{ ikon: "tamam", ton: "onay" }}
        baslik="İstek alındı"
        aciklama={mesaj}
        altNot={
          <>
            E-posta gelmediyse önce <strong className="text-notr-800">önemsiz klasörüne</strong>{" "}
            bakın. Adres kayıtlı değilse de bu ekranı görürsünüz — bilerek.
          </>
        }
      >
        <Buton boyut="genis" onClick={onGiris}>
          Girişe dön
        </Buton>
      </GirisKabugu>
    );
  }

  return (
    <GirisKabugu
      baslik="Şifremi unuttum"
      aciklama="Kurum e-posta adresinizi girin; sıfırlama bağlantısını gönderelim."
      altNot={
        <>
          E-posta gelmediyse önce <strong className="text-notr-800">önemsiz klasörüne</strong>{" "}
          bakın. Adres kayıtlı değilse de bu ekranı görürsünüz — bilerek.
        </>
      }
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          gonder.mutate();
        }}
      >
        <Alan
          etiket="E-posta"
          type="email"
          required
          value={eposta}
          onChange={(e) => setEposta(e.target.value)}
          autoComplete="email"
        />

        {gonder.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {gonder.error instanceof ApiHatasi ? gonder.error.message : "İstek gönderilemedi."}
          </p>
        )}

        <Buton
          type="submit"
          boyut="genis"
          yukleniyor={gonder.isPending}
          disabled={eposta.trim().length === 0}
        >
          Sıfırlama bağlantısı gönder
        </Buton>

        {/*
          SÜRE SUNUCUDAN GELİYOR (`@koclukpanel/contracts`).
          Buraya elle yazılmıştı ve YANLIŞTI: ekran "7 gün" diyordu,
          sunucudaki gerçek değer 30 dakikaydı. Yanlış süre, gelen
          e-postayı ertesi gün açan kullanıcıyı çalışmayan bir
          bağlantıya gönderiyordu.
        */}
        <NotKutusu ton="bilgi">
          Bağlantı <strong>{SIFRE_SIFIRLAMA_DAKIKA} dakika</strong> geçerlidir ve{" "}
          <strong>tek kullanımlıktır</strong>. Yeni bir istek, öncekini geçersiz kılar.
        </NotKutusu>

        <button
          type="button"
          onClick={onGiris}
          className="text-marka-700 hover:text-marka-600 self-center text-[13.5px] hover:underline"
        >
          Girişe dön
        </button>
      </form>
    </GirisKabugu>
  );
}
